import Phaser from 'phaser';

import {
	getTracingGameDataTextures,
	getTracingGameDecorationTextureKey,
	TRACING_GAME_CONFETTI_URL,
	TRACING_GAME_OUTSIDE_TAP_TARGET_URL,
	TRACING_GAME_TEXTURE_KEYS,
} from '../assets';
import { TRACING_GAME_SOUNDS } from '../audio';
import { EventBus, EVENTS } from '../events/tracingGameEventBus';
import { Cursor } from '../objects/Cursor';
import { Goal } from '../objects/Goal';
import { PathFollower } from '../objects/PathFollower';
import { StrokePreviewer } from '../objects/StrokePreviewer';
import { Strokes } from '../objects/Strokes';
import { TracingGameController } from '../objects/TracingGameController';
import { TracingGameSoundController } from '../objects/TracingGameSoundController';
import { VictoryPresentation } from '../objects/VictoryPresentation';
import { DEFAULT_TRACE_COLOR, TraceRenderer } from '../rendering/TraceRenderer';

import type { TracingGameData } from '../../types';

export const TRACING_GAME_SCENE_KEY = 'TracingGameScene';
export const TRACING_GAME_SCENE_WIDTH = 2048;
export const TRACING_GAME_SCENE_HEIGHT = 1536;

const DRAWING_AREA_LAYOUT = {
	horizontalInset: 0.04,
	topInset: 0.16,
	width: 0.92,
	height: 0.8,
} as const;
const DRAWING_AREA_WIDTH = TRACING_GAME_SCENE_WIDTH * DRAWING_AREA_LAYOUT.width;
const DRAWING_AREA_HEIGHT = TRACING_GAME_SCENE_HEIGHT * DRAWING_AREA_LAYOUT.height;
const DRAWING_AREA_X = TRACING_GAME_SCENE_WIDTH * DRAWING_AREA_LAYOUT.horizontalInset;
const DRAWING_AREA_Y = TRACING_GAME_SCENE_HEIGHT * DRAWING_AREA_LAYOUT.topInset;
const DRAWING_AREA_OUTLINE = { width: 5, radius: 64, color: 0x3f3f3f, alpha: 0.3 };
const TRACE_WIDTH = DRAWING_AREA_WIDTH * 0.02;
const TRACE_INPUT_DEADZONE = Math.min(DRAWING_AREA_WIDTH, DRAWING_AREA_HEIGHT) * 0.005;

type TracingGamePayload = {
	audioOn: boolean;
	debug: boolean;
	gameData?: TracingGameData | null;
};

export default class TracingGameScene extends Phaser.Scene {
	private gameContainer!: Phaser.GameObjects.Container;
	private background?: Phaser.GameObjects.TileSprite;
	private drawingAreaContainer!: Phaser.GameObjects.Container;
	private gameplayContainer!: Phaser.GameObjects.Container;
	private victoryPresentation?: VictoryPresentation;
	private strokesContainer!: Phaser.GameObjects.Container;
	private strokePreviewContainer!: Phaser.GameObjects.Container;
	private traceContainer!: Phaser.GameObjects.Container;
	private gameData!: TracingGameData;
	private debug = false;
	private controller?: TracingGameController;
	private soundEffects?: TracingGameSoundController;

	constructor() {
		super(TRACING_GAME_SCENE_KEY);
	}

	preload() {
		this.load.image(TRACING_GAME_TEXTURE_KEYS.confetti, TRACING_GAME_CONFETTI_URL);
		this.load.image(TRACING_GAME_TEXTURE_KEYS.outsideTapTarget, TRACING_GAME_OUTSIDE_TAP_TARGET_URL);
		TRACING_GAME_SOUNDS.forEach(({ key, url }) => this.load.audio(key, url));
	}

	create() {
		this.gameContainer = this.add.container(0, 0).setSize(TRACING_GAME_SCENE_WIDTH, TRACING_GAME_SCENE_HEIGHT);
		this.drawingAreaContainer = this.add
			.container(DRAWING_AREA_X, DRAWING_AREA_Y)
			.setSize(DRAWING_AREA_WIDTH, DRAWING_AREA_HEIGHT);
		this.createDrawingArea();
		this.gameplayContainer = this.add.container(0, 0);
		this.drawingAreaContainer.add(this.gameplayContainer);
		this.strokesContainer = this.add.container(0, 0);
		this.gameplayContainer.add(this.strokesContainer);
		this.strokePreviewContainer = this.add.container(0, 0);
		this.gameplayContainer.add(this.strokePreviewContainer);
		this.traceContainer = this.add.container();
		this.gameplayContainer.add(this.traceContainer);
		this.gameContainer.add(this.drawingAreaContainer);
		this.layout();

		EventBus.once(EVENTS.LOAD_GAME, this.loadGame, this);
		EventBus.on(EVENTS.SET_AUDIO_ON, this.setAudioEnabled, this);
		EventBus.on(EVENTS.RETRY_GAME, this.retryGame, this);
		this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
		this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanUp, this);
		this.events.once(Phaser.Scenes.Events.DESTROY, this.cleanUp, this);

		this.time.delayedCall(0, () => EventBus.emit(EVENTS.SCENE_READY));
	}

	private createDrawingArea() {
		const { width, radius, color, alpha } = DRAWING_AREA_OUTLINE;
		const inset = width / 2;
		const outline = this.add
			.graphics()
			.lineStyle(width, color, alpha)
			.strokeRoundedRect(inset, inset, DRAWING_AREA_WIDTH - width, DRAWING_AREA_HEIGHT - width, radius - inset);
		this.drawingAreaContainer.add(outline);
	}

	private loadGame({ audioOn, debug, gameData }: TracingGamePayload) {
		if (!gameData?.data?.strokes?.length) {
			throw new Error('Tracing game data must contain at least one stroke');
		}

		this.setAudioEnabled(audioOn);
		this.debug = debug;
		this.gameData = gameData;
		this.loadDataTextures(gameData);
	}

	private loadDataTextures(gameData: TracingGameData) {
		let hasQueuedTexture = false;

		getTracingGameDataTextures(gameData.presentation).forEach(({ url, textureKey }) => {
			if (!url || this.textures.exists(textureKey)) {
				return;
			}

			this.load.image(textureKey, url);
			hasQueuedTexture = true;
		});

		if (!hasQueuedTexture) {
			this.createLoadedGameObjects();
			return;
		}

		this.load.once(Phaser.Loader.Events.COMPLETE, this.createLoadedGameObjects, this);
		this.load.start();
	}

	private createLoadedGameObjects() {
		this.createBackground();
		this.createDecorations();
		const soundEffects = new TracingGameSoundController(this);
		this.soundEffects = soundEffects;
		const traceColor = this.gameData.presentation.trace?.ink_color ?? DEFAULT_TRACE_COLOR;
		const traceRenderer = new TraceRenderer({
			scene: this,
			parent: this.traceContainer,
			width: TRACE_WIDTH,
			color: traceColor,
		});
		const strokePreviewer = new StrokePreviewer({
			scene: this,
			parent: this.strokePreviewContainer,
			color: traceColor,
		});

		const strokes = new Strokes({
			scene: this,
			parent: this.strokesContainer,
			debugParent: this.gameplayContainer,
			rewardParent: this.gameplayContainer,
			rewardTextureKey: TRACING_GAME_TEXTURE_KEYS.strokeElement,
			data: this.gameData.data.strokes,
			debug: this.debug,
			drawingAreaWidth: DRAWING_AREA_WIDTH,
			drawingAreaHeight: DRAWING_AREA_HEIGHT,
			checkpointMaxDistance:
				Math.min(DRAWING_AREA_WIDTH, DRAWING_AREA_HEIGHT) * this.gameData.settings.checkpoint_max_distance,
			maxInkMultiplier: this.gameData.settings.max_ink,
			color: this.gameData.presentation.stroke?.color,
			opacity: this.gameData.presentation.stroke?.opacity,
		});

		const startPosition = strokes.getActiveStartPosition();
		const goalPosition = strokes.getFinalEndPosition();

		const pathFollower = new PathFollower({
			scene: this,
			parent: this.gameplayContainer,
			x: startPosition.x,
			y: startPosition.y,
			textureKey: TRACING_GAME_TEXTURE_KEYS.pathFollower,
			rewardTextureKey: TRACING_GAME_TEXTURE_KEYS.rewardElement,
			soundEffects,
			pivot: this.gameData.presentation.path_follower?.image_pivot,
		});

		const goal = new Goal({
			scene: this,
			parent: this.gameplayContainer,
			x: goalPosition.x,
			y: goalPosition.y,
			direction: strokes.getFinalEndDirection(),
			strokeOffset: strokes.getFinalGoalOffset(),
			textureKey: TRACING_GAME_TEXTURE_KEYS.goal,
			pivot: this.gameData.presentation.goal?.image_pivot,
		});

		const cursor = new Cursor({
			scene: this,
			parent: this.gameplayContainer,
			x: startPosition.x,
			y: startPosition.y,
			boundsWidth: DRAWING_AREA_WIDTH,
			boundsHeight: DRAWING_AREA_HEIGHT,
			onTraceStart: (x, y) => this.controller?.startTrace(x, y),
			onTrace: (fromX, fromY, toX, toY) =>
				this.controller?.drawTrace(fromX, fromY, toX, toY) ?? { x: fromX, y: fromY },
			onTraceEnd: () => this.controller?.endTrace(),
		});
		this.controller = new TracingGameController({
			strokes,
			cursor,
			pathFollower,
			goalPosition: { x: goal.image.x, y: goal.image.y },
			strokePreviewer,
			traceRenderer,
			soundEffects,
			inputDeadzone: TRACE_INPUT_DEADZONE,
			onStart: () => EventBus.emit(EVENTS.GAME_STARTED),
			onFailure: (onComplete) => EventBus.emit(EVENTS.FAILURE, onComplete),
			onVictory: () => EventBus.emit(EVENTS.VICTORY),
			onFinish: (event) => EventBus.emit(EVENTS.GAME_FINISHED, event),
			resetVictory: () => this.resetVictory(),
			playVictory: (score, onComplete) => {
				this.victoryPresentation = new VictoryPresentation({
					scene: this,
					parent: this.gameContainer,
					gameplayContainer: this.gameplayContainer,
					width: TRACING_GAME_SCENE_WIDTH,
					height: TRACING_GAME_SCENE_HEIGHT,
					soundEffects,
				});
				this.victoryPresentation.play(
					{ x: DRAWING_AREA_X + goal.image.x, y: DRAWING_AREA_Y + goal.image.y },
					score,
					onComplete,
				);
			},
		});
		this.controller.start();
	}

	private createDecorations() {
		(this.gameData.presentation.decorations ?? []).forEach(({ decoration_position: [x, y] }, index) => {
			const textureKey = getTracingGameDecorationTextureKey(index);
			if (!this.textures.exists(textureKey)) return;

			const decoration = this.add.image(
				DRAWING_AREA_X + x * DRAWING_AREA_WIDTH,
				DRAWING_AREA_Y + y * DRAWING_AREA_HEIGHT,
				textureKey,
			);
			this.gameContainer.addAt(decoration, 0);
		});
	}

	private createBackground() {
		if (!this.textures.exists(TRACING_GAME_TEXTURE_KEYS.background)) {
			return;
		}

		this.background = this.add
			.tileSprite(0, 0, this.scale.width, this.scale.height, TRACING_GAME_TEXTURE_KEYS.background)
			.setOrigin(0)
			.setDepth(-1);
		this.layout();
	}

	private retryGame() {
		this.controller?.retry();
	}

	private resetVictory() {
		this.victoryPresentation?.destroy();
		this.victoryPresentation = undefined;
	}

	private setAudioEnabled(audioOn: boolean) {
		this.sound.mute = !audioOn;
	}

	private layout() {
		const containerScale = Math.min(
			this.scale.width / TRACING_GAME_SCENE_WIDTH,
			this.scale.height / TRACING_GAME_SCENE_HEIGHT,
		);
		const containerX = (this.scale.width - TRACING_GAME_SCENE_WIDTH * containerScale) / 2;
		const containerY = (this.scale.height - TRACING_GAME_SCENE_HEIGHT * containerScale) / 2;

		this.gameContainer.setPosition(containerX, containerY).setScale(containerScale);
		this.background
			?.setSize(this.scale.width, this.scale.height)
			.setTileScale(containerScale)
			.setTilePosition(-containerX / containerScale, -containerY / containerScale);
	}

	private cleanUp() {
		this.controller?.dispose();
		this.controller = undefined;
		this.soundEffects?.destroy();
		this.soundEffects = undefined;
		EventBus.off(EVENTS.LOAD_GAME, this.loadGame, this);
		EventBus.off(EVENTS.SET_AUDIO_ON, this.setAudioEnabled, this);
		EventBus.off(EVENTS.RETRY_GAME, this.retryGame, this);
		this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this);
		this.load.off(Phaser.Loader.Events.COMPLETE, this.createLoadedGameObjects, this);
	}
}
